import {
  OnboardingApprovalStatusRow,
  WsApprovalKind,
  WsApprovalStatus,
} from "services/work-security-approvals.service";

/** i18n key of an approval's type — Įdarbinimas / Sutarties pakeitimas / Profilis. */
export function approvalKindKey(kind: WsApprovalKind | undefined | null): string {
  if (kind === "amendment") return "ws_approvals_kind_amendment";
  if (kind === "profile") return "ws_approvals_kind_profile";
  return "ws_approvals_kind_hire";
}

/** «24.09» — the short day.month the status line uses. */
export function formatDayMonth(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  const date = new Date(typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value);
  if (Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}`;
}

/**
 * The live approval per sphere: superseded rows are history, and when a
 * sphere somehow has two live rows the pending one is the one that matters.
 * Spheres keep the order the backend sent them in.
 */
export function currentApprovalsByArea(
  rows: OnboardingApprovalStatusRow[]
): OnboardingApprovalStatusRow[] {
  const byArea = new Map<string, OnboardingApprovalStatusRow>();
  rows.forEach((row) => {
    if (row.status === "superseded") return;
    const key = row.area?.id || "";
    const existing = byArea.get(key);
    if (!existing || (existing.status !== "pending" && row.status === "pending")) {
      byArea.set(key, row);
    }
  });
  return Array.from(byArea.values());
}

export interface ApprovalStatusSegment {
  areaId: string;
  areaName: string;
  status: Exclude<WsApprovalStatus, "superseded">;
  /** Who approved + when (approved), or who is being waited for (pending). */
  detail: string;
}

/**
 * «Darbų sauga ✓ Rolandas 24.09 · Kibernetinis saugumas — laukia (Violeta)»,
 * as data: one segment per sphere, the caller renders the marks and words.
 */
export function approvalStatusSegments(rows: OnboardingApprovalStatusRow[]): ApprovalStatusSegment[] {
  return currentApprovalsByArea(rows).map((row) => {
    const status = row.status as ApprovalStatusSegment["status"];
    let detail = "";
    if (status === "approved") {
      detail = [row.approvedBy?.name, formatDayMonth(row.approvedAt)].filter(Boolean).join(" ");
    } else if (status === "pending") {
      detail = (row.responsibles || []).map((person) => person.name).filter(Boolean).join(", ");
    }
    return {
      areaId: row.area?.id || "",
      areaName: row.area?.name || "",
      status,
      detail,
    };
  });
}

/** True while some sphere that gates hiring still waits for its approver. */
export function hasPendingBlockingApproval(rows: OnboardingApprovalStatusRow[]): boolean {
  return currentApprovalsByArea(rows).some((row) => row.status === "pending" && row.isBlocking);
}

type WsStepStatusLike = {
  requiredUnsignedCount?: number;
  /** Backend verdict: at work_security, no required document unsigned, no blocking approval pending. */
  canComplete?: boolean;
  /** Blocking per-sphere approvals still pending for this employee (backend count). */
  pendingBlockingApprovals?: number;
};

/**
 * True while hiring waits for a sphere approval. The backend's
 * `pendingBlockingApprovals` is the authority (it counts across the
 * employee's approvals, not only the rows this card lists); the rows are only
 * a fallback for a response without the field.
 */
export function isWaitingForBlockingApprovals(
  wsStatus: WsStepStatusLike | null | undefined,
  approvals: OnboardingApprovalStatusRow[]
): boolean {
  if (typeof wsStatus?.pendingBlockingApprovals === "number") {
    return wsStatus.pendingBlockingApprovals > 0;
  }
  return hasPendingBlockingApproval(approvals);
}

/**
 * Whether the work_security step card still offers the manual «Užbaigti».
 * The step skips itself once no blocking sphere is pending and no required
 * document is unsigned — no required pareigybės included; the button is left
 * for a record still waiting at the step (e.g. from before that rule). The
 * backend decides: `canComplete` is exactly "nothing blocks any more"; a
 * required role without any document is nothing to sign and needs no
 * confirmation.
 */
export function shouldOfferManualWsComplete(
  wsStatus: WsStepStatusLike | null | undefined,
  approvals: OnboardingApprovalStatusRow[]
): boolean {
  if (!wsStatus) return false;
  if (typeof wsStatus.canComplete === "boolean") return wsStatus.canComplete;
  // Response without the verdict: derive the same rule locally.
  if (isWaitingForBlockingApprovals(wsStatus, approvals)) return false;
  return (wsStatus.requiredUnsignedCount || 0) === 0;
}
